import { Injectable, WritableSignal, signal, inject } from '@angular/core';

import { Md5 } from 'ts-md5';

import { Language } from '@models/language.model';
import { SsrService } from '@services/ssr.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalsService {
  private ssrService = inject(SsrService);
  public token: WritableSignal<string> = signal('');
  public headerHash: WritableSignal<{}> = signal({});
  public headerHashLogged: WritableSignal<{}> = signal({});
  public headerHashMD5: WritableSignal<{}> = signal({});
  public cacheTimer: number = 600000; // 10min
  public cacheTimerLong: number = 43200000; // 12hours
  public logged: WritableSignal<boolean> = signal(false);
  public formUniqueId: WritableSignal<string | undefined> = signal(undefined);
  public previousUrl: WritableSignal<null | string> = signal(null);
  public currentUrl: WritableSignal<null | string> = signal(null);

  setHeaderHash(language: Language): void {
    if (this.ssrService.isServer()) {
      this.headerHash.set({ 'Accept-Language': language, Accept: 'application/json', 'Content-Type': 'application/json', 'x-client-hash': 'jqX9VQfjhcvFR4tV' });
    } else {
      this.headerHash.set({ 'Accept-Language': language, Accept: 'application/json', 'Content-Type': 'application/json' });
    }
  }

  setHeaderHashLogged(token: string): void {
    this.token.set(token);
    const header = { ...{ 'X-Client-Token': token }, ...this.headerHash() };
    this.headerHashLogged.set(header);
  }

  setHeaderWithMD5Hash(email: string): void {
    const header = { ...{ 'X-User-Token-Old': Md5.hashStr(email).toString() }, ...this.headerHashLogged() };
    this.headerHashMD5.set(header);
  }

  jsonToString(data: {}): string {
    return JSON.stringify(data).replace(/"/g, "'");
  }

  getFragmentParams(value: string | null): { [key: string]: string | null } {
    const params: { [key: string]: string | null } = {};
    if (value) {
      const parts = value.split('&');
      for (const part of parts) {
        const [key, val] = part.split('=');

        params[key] = val === 'NaN' ? null : val;
      }
    }
    return params;
  }

  removeDiacritics(content: string): string {
    const text = content
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .match(/[^_\W]+/g)
      ?.join(' ')
      .replace(/ /g, '-');
    return text ?? '';
  }

  searchPhoneCountryCode(phone: number | string | undefined): number | undefined {
    if (!phone) return undefined;
    const phoneString = phone.toString();
    switch (phoneString) {
      case '420':
        return 44;
      case '421':
        return 24;
      case '48':
        return 19;
      default:
        return 44;
    }
  }

  generateString(): string {
    const unixTimestamp = Math.floor(Date.now() / 1000);
    const randomThreeDigitNumber = Math.floor(Math.random() * (999 - 100 + 1)) + 100;
    const resultString = unixTimestamp.toString() + randomThreeDigitNumber.toString() + '016';
    this.formUniqueId.set(resultString);
    return resultString;
  }

  updateUrl(url: string | null): void {
    this.previousUrl.set(this.currentUrl());
    this.currentUrl.set(url);
  }

  shortenText(text: string, maxLength: number): { text: string; changed: boolean } {
    if (text.length <= maxLength) return { text: text, changed: false };
    let shortText = text.slice(0, maxLength);
    const lastSpaceIndex = shortText.lastIndexOf(' ');
    if (shortText.lastIndexOf(' ')) shortText = shortText.slice(0, lastSpaceIndex);
    return { text: shortText + '...', changed: true };
  }

  createTimeOptions(): string[] {
    const options: string[] = [];
    for (let hour = 9; hour < 19; hour++) {
      const hourStr = hour.toString().padStart(2, '0');
      options.push(`${hourStr}:00`);
      options.push(`${hourStr}:15`);
      options.push(`${hourStr}:30`);
      options.push(`${hourStr}:45`);
    }
    options.push('19:00');
    return options;
  }

  replacePlaceholderPhoto(url: string, size: string): string {
    if (url === 'https://www.aaaauto.cz/img/nophoto/nophoto-mototechna-v3-big-inverse.jpg' || !url) {
      return `https://aaaautoczimg.vshcdn.net/new/13036_${size}.jpg`;
    } else {
      return url;
    }
  }
}
